import { Box, Flex, Group, Text } from "@mantine/core"
import { ReactNode } from "react"

type Props = {
  /** Leading icon (Tabler icon, ThemeIcon, etc.) */
  icon?: ReactNode
  label: ReactNode
  value: ReactNode
  /** Bolder value text — used for emphasized rows like totals */
  bold?: boolean
  /** Tighter font sizing — useful inside dense sidebars (default 14, the UI standard minimum) */
  size?: number
  /**
   * Allow long values to wrap to multiple lines instead of nowrap.
   * Use for free-text fields (notes, payment-term descriptions); keep
   * default `false` for prices/dates/codes that must stay on one line.
   */
  wrap?: boolean
}

/**
 * Horizontal info row: `[icon] label …………… value` layout, used in
 * detail sidebars / payment summaries / sticky info cards. Label sits
 * on the left with the icon, value pinned to the right with white-space
 * nowrap so prices/codes never wrap mid-string. Pass `wrap` for
 * long free-text values that should wrap inside the parent card.
 */
export function InfoRow({ icon, label, value, bold = false, size = 14, wrap = false }: Props) {
  return (
    <Flex justify="space-between" align={wrap ? "flex-start" : "center"} gap={8} style={{ minWidth: 0 }}>
      <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
        {icon && (
          <Box c="var(--mantine-color-gray-6)" style={{ display: "flex" }}>
            {icon}
          </Box>
        )}
        <Text fz={size} c="var(--mantine-color-gray-6)">
          {label}
        </Text>
      </Group>
      <Text
        fz={size}
        fw={bold ? 600 : 500}
        c="var(--mantine-color-dark-7)"
        ta="right"
        style={
          wrap
            ? { minWidth: 0, flex: 1, whiteSpace: "normal", wordBreak: "break-word", fontVariantNumeric: "tabular-nums" }
            : { whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }
        }
      >
        {value}
      </Text>
    </Flex>
  )
}
